import clsx from 'clsx'
import { useEffect, useRef, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { CapabilityCurationPanel } from '@/components/marketplace/admin/CapabilityCurationPanel.tsx'
import { ConnectedSystemsPanel } from '@/components/marketplace/admin/ConnectedSystemsPanel.tsx'
import { CurationPanel } from '@/components/marketplace/admin/CurationPanel.tsx'
import { PoliciesPanel } from '@/components/marketplace/admin/PoliciesPanel.tsx'
import { ReportsQueue } from '@/components/marketplace/admin/ReportsQueue.tsx'
import { SettingsForm } from '@/components/marketplace/admin/SettingsForm.tsx'
import { EmptyState } from '@/components/ui/primitives.tsx'
import { useSession } from '@/hooks/use-api.ts'
import { useT } from '@/i18n/index.ts'
import { AgentsPage } from '@/pages/AgentsPage.tsx'

type Tab = 'definitions' | 'reports' | 'curation' | 'systems' | 'capabilities' | 'policies' | 'settings'

/**
 * Reading order is the administrator's day: triage, curate, connect, tune, configure.
 *
 * There is no review queue here any more. What used to wait for an administrator's decision —
 * an agent going public — is a sharing request now (PRD-26 D-13), decided with the knowledge
 * bases' requests in one queue under Governance, so a link to the old tab lands there.
 *
 * "Systems" sits immediately before "Capabilities", and that ordering is a dependency rather
 * than a preference. Capabilities is the wording backlog for things that have already been
 * discovered and switched on; before this tab existed it was empty on every fresh deployment
 * and nothing in the product could ever fill it, because registering a system was an HTTP
 * request somebody had to compose by hand.
 */
const TABS: Tab[] = ['definitions', 'reports', 'curation', 'systems', 'capabilities', 'policies', 'settings']

/**
 * Whether a tab's label comes from the studio's own namespace.
 *
 * The four phase-4 labels live in `admin.studio.tabs` rather than beside the tab bar, which
 * is where they used to be — a module-scope `Record` compiled at import, i.e. the capture
 * that renders the previous language after a locale switch with nothing failing anywhere
 * (PRD-27 F-3). The predicate stays module-scope because it is a question about the tab
 * union, not about words; the lookup itself happens inside the component body.
 *
 * They are kept apart from the maker namespace's tab labels so the two curation surfaces can
 * be told apart by name: "Curation" is listings (featured order, Official, suspension) and
 * "Capabilities" is the wording backlog for connected capabilities. Two tabs both called
 * curation would be a coin flip every time an administrator went looking for one of them.
 */
const isStudioTab = (tab: Tab): tab is 'reports' | 'systems' | 'capabilities' | 'policies' =>
  tab === 'reports' || tab === 'systems' || tab === 'capabilities' || tab === 'policies'

/**
 * The administrator studio.
 *
 * "Definitions" is the pre-existing agent studio, embedded unmodified: the marketplace did
 * not replace the definition editor, it grew a people-facing plane in front of it, and an
 * admin still needs the surface that shows a definition against its resolve preview.
 * Rendering the component rather than re-implementing it is what keeps that true as the
 * studio changes.
 *
 * The tab labels are load-bearing text, not decoration: the browser suite reaches "Curation"
 * and "Systems" by accessible name, and a count badge inside one of those buttons would
 * rename it. Whatever a queue has to say about its own size, it says on its own panel.
 */
export const AgentsStudioPage = () => {
  const studio = useT().admin.studio
  const frame = studio.frame
  const tabLabel = (tab: Tab): string => (isStudioTab(tab) ? studio.tabs[tab] : frame.tabs[tab])
  const session = useSession()
  const roles = session.data?.user?.roles ?? []
  const isAdmin = roles.includes('admin')
  /**
   * Opens on the tab the URL names, when it names one this page has.
   *
   * The studio's tabs are state rather than routes, which is right for a person clicking
   * between them and wrong for the one case that arrives from outside: the OAuth callback
   * redirects a browser here after somebody approved a sign-in at a third party, and landing
   * them on "Definitions" with no acknowledgement is the worst possible end to that
   * sequence — they have just granted something and the product says nothing.
   *
   * Read once, from the initialiser, so a later `replaceState` that tidies the parameters
   * away cannot bounce the reader back to the first tab.
   */
  const [requested] = useState(() => new URLSearchParams(window.location.search).get('tab'))
  const [tab, setTab] = useState<Tab>(() => (TABS.includes(requested as Tab) ? (requested as Tab) : 'definitions'))

  // On a phone the lane scrolls, and a link to `?tab=settings` would otherwise open on a panel
  // whose tab sits off to the right, with nothing on screen saying which tab this is.
  const lane = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const strip = lane.current
    const active = strip?.querySelector<HTMLElement>(`#studio-tab-${tab}`)
    if (!strip || !active) return
    const bounds = strip.getBoundingClientRect()
    const box = active.getBoundingClientRect()
    if (box.left < bounds.left || box.right > bounds.right) {
      strip.scrollLeft += box.left - bounds.left - (bounds.width - box.width) / 2
    }
  }, [tab])

  // The review queue's old address: an agent waiting to go public is a sharing request now.
  if (requested === 'reviews') return <Navigate to="/governance?view=sharing" replace />

  if (!isAdmin) {
    return (
      <div className="fade-in min-h-0 flex-1 overflow-y-auto px-5 py-10">
        <EmptyState title={frame.adminOnly} body={frame.adminOnlyBody} />
      </div>
    )
  }

  return (
    <div className="fade-in flex min-h-0 flex-1 flex-col">
      <header className="shrink-0 border-b border-rule px-5 pt-5 sm:px-8">
        <h1 className="display text-[30px] sm:text-[38px]">{frame.title}</h1>
        <p className="mt-[7px] max-w-[560px] text-pretty text-[13.5px] leading-[1.5] text-fg-muted">{frame.lead}</p>

        {/* Seven pills do not fit a phone. They scroll inside their own lane rather than
            widening the page, which is the one thing the layout must never do. The lane keeps
            padding on every side because `overflow-x` clips vertically too, and a focus ring
            is a box-shadow — without the clearance the keyboard user's ring is sliced off. */}
        <div ref={lane} className="-mx-5 mt-3 overflow-x-auto px-5 pt-1.5 pb-3.5 sm:-mx-8 sm:px-8">
          <div role="tablist" aria-label={frame.title} className="flex w-max gap-1.5">
            {TABS.map((value) => (
              <button
                key={value}
                type="button"
                role="tab"
                id={`studio-tab-${value}`}
                aria-selected={tab === value}
                aria-controls={`studio-panel-${value}`}
                onClick={() => setTab(value)}
                className={clsx(
                  'h-[30px] shrink-0 rounded-full border px-3.5 text-[12.5px] tracking-[-0.01em]',
                  'transition-[color,background-color,border-color,transform] duration-200 ease-out',
                  tab === value
                    ? 'border-rule-strong bg-card-solid font-semibold text-fg'
                    : 'border-transparent text-fg-muted hover:-translate-y-px hover:border-rule-strong hover:text-fg'
                )}
              >
                {tabLabel(value)}
              </button>
            ))}
          </div>
        </div>
      </header>

      {tab === 'definitions' ? (
        <div
          role="tabpanel"
          id="studio-panel-definitions"
          aria-labelledby="studio-tab-definitions"
          className="flex min-h-0 flex-1"
        >
          <AgentsPage />
        </div>
      ) : (
        <div
          key={tab}
          role="tabpanel"
          id={`studio-panel-${tab}`}
          aria-labelledby={`studio-tab-${tab}`}
          className="fade-in min-h-0 flex-1 overflow-y-auto"
        >
          <div className="mx-auto w-full max-w-5xl px-5 py-6 sm:px-8">
            {tab === 'reports' && <ReportsQueue enabled={isAdmin} />}
            {tab === 'curation' && <CurationPanel enabled={isAdmin} />}
            {tab === 'systems' && <ConnectedSystemsPanel enabled={isAdmin} />}
            {tab === 'capabilities' && <CapabilityCurationPanel enabled={isAdmin} />}
            {tab === 'policies' && <PoliciesPanel enabled={isAdmin} />}
            {tab === 'settings' && <SettingsForm enabled={isAdmin} />}
          </div>
        </div>
      )}
    </div>
  )
}
